import type { ReactElement, ReactNode } from "react";
import type { FremdKarte, Fremdtickets, TicketZeile } from "@kundeninfo/api-client";
import { Icon, useSofortPopup } from "@kundeninfo/ui";
import { Boardspalte } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { zeileFuer, zeilenText } from "./ticketFormat.js";
import { FREMD_SPALTE_ID, standText, uhrzeitText } from "./fremdSicht.js";

/**
 * DIE FREMDSPALTE (Spec Tickets, Nachtrag 1f, F142, Frage 225): eigene Spalte ganz links mit derselben
 * Zeitleiste wie jede Spalte — ein Fremdticket für Dienstag steht in der Zeile Dienstag, ohne Fälligkeit in
 * „Jetzt" (226). Gezeigt werden nur Zeilen mit Fremdtickets (die Spalte ist kein Ablageziel, leere Tage tragen
 * nichts). Zeilen `li.tfz__zeile` OHNE `data-ablage`: Die Ziehhilfen kennen nur `li.tz__zeile`, und
 * `ziehOptionen.erlaubt` verbietet die Spalte ohnehin (Review Focus 4). Zuklappen wie jede Spalte (gemerkt).
 * „Jetzt abgleichen" nur für die eigenen Fremdtickets (F141, F147: ohne `onAbgleich` kein Knopf, etwa im ADMIN-Blick
 * auf eine andere Person); gesperrt mit Sofort-Popup „Wieder möglich ab 14:32", sonst „Stand von 14:02".
 */
export function FremdSpalte(p: {
  daten: Fremdtickets; zeilen: readonly TicketZeile[]; heute: string; zugeklappt: boolean;
  onUmgeschaltet: (zu: boolean) => void; karte: (k: FremdKarte) => ReactNode; onAbgleich?: () => void;
  gesperrtBis: string | null;
}): ReactElement {
  const ueberfaellig = p.daten.items.filter((k) => k.dueDate !== null && k.dueDate < p.heute).length;
  const gruppen = new Map<number, FremdKarte[]>();
  for (const k of p.daten.items) {
    const i = zeileFuer(k.dueDate, p.zeilen, p.heute);
    gruppen.set(i, [...(gruppen.get(i) ?? []), k]);
  }
  const gesperrt = p.gesperrtBis !== null && Date.parse(p.gesperrtBis) > Date.now();
  const popup = useSofortPopup<HTMLSpanElement>(gesperrt ? `Wieder möglich ab ${uhrzeitText(p.gesperrtBis!)}`
    : `Jetzt abgleichen${p.daten.letzterAbgleich === null ? "" : ` · ${standText(p.daten.letzterAbgleich)}`}`);
  return (
    <Boardspalte id={FREMD_SPALTE_ID} name="Fremdtickets" zaehler={p.daten.items.length} fehler={ueberfaellig}
      fehlerText="überfällig" randlos zugeklappt={p.zugeklappt} onUmgeschaltet={(d) => p.onUmgeschaltet(d.zugeklappt)}
      menue={p.onAbgleich === undefined ? undefined : (
        <span ref={popup}>
          <KiKnopf art="leise" name="Fremdtickets jetzt abgleichen" deaktiviert={gesperrt} onClick={p.onAbgleich}>
            <Icon name="arrow-rotate-right" size={14} />
          </KiKnopf>
        </span>
      )}>
      <ol className="tfz" aria-label="Zeitleiste Fremdtickets">
        {p.zeilen.filter((z) => z.art === "JETZT" || (gruppen.get(z.index) ?? []).length > 0).map((z) => {
          const karten = gruppen.get(z.index) ?? [];
          return (
            <li key={z.index} className={`tfz__zeile tfz__zeile--${z.art.toLowerCase()}`} data-zeile={z.index}>
              <div className="tz__kopf">
                <span className="tz__beschriftung">{zeilenText(z)}</span>
                {karten.length > 0 && <span className="tz__anzahl" aria-hidden="true">{karten.length}</span>}
              </div>
              {karten.length > 0 && <div className="tz__karten">{karten.map((k) => p.karte(k))}</div>}
            </li>
          );
        })}
      </ol>
    </Boardspalte>
  );
}
